// FHIR Client for Epic integration
// Handles authenticated FHIR API calls

import {
  FHIRPatient,
  FHIRCoverage,
  FHIREncounter,
  FHIRCondition,
  FHIRBundle,
} from './types';
import { getAccessToken, getFHIRBaseUrl } from './smartLauncher';
import { extractResourcesFromBundle, aggregateFHIRContext, AggregatedFHIRContext } from './fhirMappers';

/**
 * Make an authenticated FHIR API request
 */
async function fhirFetch<T>(
  endpoint: string,
  clientId: string,
  options: RequestInit = {}
): Promise<T> {
  const fhirBaseUrl = getFHIRBaseUrl();
  if (!fhirBaseUrl) {
    throw new Error('No FHIR base URL available');
  }
  
  const accessToken = await getAccessToken(clientId);
  if (!accessToken) {
    throw new Error('No valid access token available');
  }
  
  const url = endpoint.startsWith('http') ? endpoint : `${fhirBaseUrl}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Accept': 'application/fhir+json',
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`FHIR request failed: ${response.status} - ${error}`);
  }
  
  return response.json();
}

/**
 * Fetch Patient resource by ID
 */
export async function fetchPatient(
  patientId: string,
  clientId: string
): Promise<FHIRPatient> {
  return fhirFetch<FHIRPatient>(`/Patient/${patientId}`, clientId);
}

/**
 * Fetch Coverage resources for a patient
 */
export async function fetchCoverages(
  patientId: string,
  clientId: string
): Promise<FHIRCoverage[]> {
  const bundle = await fhirFetch<FHIRBundle<FHIRCoverage>>(
    `/Coverage?patient=${patientId}&status=active`,
    clientId
  );
  
  return extractResourcesFromBundle(bundle, 'Coverage');
}

/**
 * Fetch Condition resources for a patient
 * Filters to encounter-diagnosis category for active problems
 */
export async function fetchConditions(
  patientId: string,
  clientId: string,
  encounterId?: string
): Promise<FHIRCondition[]> {
  let endpoint = `/Condition?patient=${patientId}&clinical-status=active`;
  
  // If encounter context, fetch encounter-specific diagnoses
  if (encounterId) {
    endpoint += `&encounter=Encounter/${encounterId}`;
  }
  
  const bundle = await fhirFetch<FHIRBundle<FHIRCondition>>(endpoint, clientId);
  
  return extractResourcesFromBundle(bundle, 'Condition');
}

/**
 * Fetch Encounter resource by ID
 */
export async function fetchEncounter(
  encounterId: string,
  clientId: string
): Promise<FHIREncounter> {
  return fhirFetch<FHIREncounter>(`/Encounter/${encounterId}`, clientId);
}

/**
 * Fetch all relevant context for a patient
 * Combines Patient, Coverage, Condition, and optionally Encounter
 */
export async function fetchFullContext(
  patientId: string,
  clientId: string,
  encounterId?: string
): Promise<AggregatedFHIRContext> {
  // Fetch all resources in parallel
  const promises: Promise<unknown>[] = [
    fetchPatient(patientId, clientId),
    fetchCoverages(patientId, clientId),
    fetchConditions(patientId, clientId, encounterId),
  ];
  
  if (encounterId) {
    promises.push(fetchEncounter(encounterId, clientId));
  }
  
  const results = await Promise.allSettled(promises);
  
  // Extract successful results
  const patient = results[0].status === 'fulfilled' 
    ? results[0].value as FHIRPatient 
    : null;
  
  const coverages = results[1].status === 'fulfilled' 
    ? results[1].value as FHIRCoverage[] 
    : null;
  
  const conditions = results[2].status === 'fulfilled' 
    ? results[2].value as FHIRCondition[] 
    : null;
  
  const encounter = encounterId && results[3]?.status === 'fulfilled'
    ? results[3].value as FHIREncounter
    : null;
  
  // Log any errors for debugging
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.warn(`FHIR fetch ${index} failed:`, result.reason);
    }
  });
  
  return aggregateFHIRContext(patient, coverages, conditions, encounter);
}

/**
 * Search for patients by name or MRN
 * (Used when not in SMART launch context)
 */
export async function searchPatients(
  query: string,
  clientId: string
): Promise<FHIRPatient[]> {
  // Try name search first, then identifier
  const bundle = await fhirFetch<FHIRBundle<FHIRPatient>>(
    `/Patient?name=${encodeURIComponent(query)}&_count=10`,
    clientId
  );
  
  return extractResourcesFromBundle(bundle, 'Patient');
}

/**
 * Refresh patient context (e.g., after demographic update)
 */
export async function refreshPatientContext(
  patientId: string,
  clientId: string
): Promise<FHIRPatient> {
  return fetchPatient(patientId, clientId);
}
